import React, { useContext, useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CreditCard, Loader2, X } from "lucide-react";
import { IntlContext } from "context";
import dataFetcher from "hooks/bookService";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import OfferPaymentForm from "./OfferPaymentForm";

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    bookingId: string;
    providerId: string;
    clientId: string;
    hourlyRate: number;
    nbHours: number;
    cesuPlusActive: boolean;
    amountToPay: number;
    onSuccess: () => void;
};

type PaymentLoadState = "idle" | "loading" | "ready" | "error";

const formatEuro = (value: number): string => `${value.toFixed(2)} €`;

const OfferPaymentModal: React.FC<Props> = ({
    open,
    onOpenChange,
    bookingId,
    providerId,
    clientId,
    hourlyRate,
    nbHours,
    cesuPlusActive,
    amountToPay,
    onSuccess,
}) => {
    const intl = useContext(IntlContext);
    const [clientSecret, setClientSecret] = useState("");
    const [loadState, setLoadState] = useState<PaymentLoadState>("idle");
    const [loadError, setLoadError] = useState<string | null>(null);

    const stripePromise = useMemo(() => {
        const publishableKey = process.env.REACT_APP_STRIPE_API_KEY_PUBLIC;
        return publishableKey ? loadStripe(publishableKey) : null;
    }, []);

    useEffect(() => {
        if (!open) {
            setClientSecret("");
            setLoadState("idle");
            setLoadError(null);
            return;
        }

        if (!bookingId) {
            return;
        }

        if (!stripePromise) {
            setLoadState("error");
            setLoadError(intl.formatMessage("booking.messages.confirmation.payment.stripe_not_configured"));
            return;
        }

        let cancelled = false;
        setLoadState("loading");
        setLoadError(null);
        setClientSecret("");

        void dataFetcher
            .createPayment({ id: bookingId })
            .then((paymentIntent) => {
                if (cancelled) {
                    return;
                }

                const secret = paymentIntent.client_secret;
                if (!secret) {
                    setLoadState("error");
                    setLoadError(intl.formatMessage("booking.messages.confirmation.payment.load_error"));
                    return;
                }

                setClientSecret(secret);
                setLoadState("ready");
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }

                setLoadState("error");
                setLoadError(
                    intl.formatMessage("booking.messages.confirmation.payment.load_error"),
                );
            });

        return () => {
            cancelled = true;
        };
    }, [open, bookingId, stripePromise, intl]);

    const handleSuccess = () => {
        onSuccess();
        onOpenChange(false);
    };

    const stripeOptions = clientSecret ? { clientSecret, appearance: { theme: "stripe" as const } } : undefined;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[min(90dvh,900px)] max-w-md flex-col gap-0 overflow-hidden p-0 sm:rounded-2xl [&>button]:hidden">
                <div className="shrink-0 border-b border-slate-100 px-6 py-6">
                    <div className="flex items-start gap-4">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[#00bc7d] text-white shadow-sm">
                            <CreditCard className="size-6" strokeWidth={2.25} aria-hidden="true" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <DialogHeader className="space-y-1 text-left">
                                <DialogTitle className="text-xl font-black text-gray-900">
                                    {intl.formatMessage("booking.messages.confirmation.payment.title")}
                                </DialogTitle>
                                <DialogDescription className="text-sm text-slate-500">
                                    {intl.formatMessage("booking.messages.confirmation.payment.subtitle")}
                                </DialogDescription>
                            </DialogHeader>
                        </div>
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="flex size-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                            aria-label={intl.formatMessage("booking.messages.confirmation.payment.close")}
                        >
                            <X className="size-4" />
                        </button>
                    </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-6 py-6">
                    <div className="flex items-center justify-between rounded-xl border border-[#e0e7ff] bg-[#eef2ff] px-4 py-3">
                        <span className="text-[13px] font-medium text-[#372aac]">
                            {intl.formatMessage("booking.messages.confirmation.payment.amount_label")}
                        </span>
                        <span className="text-[18px] font-black text-[#312c85]">{formatEuro(amountToPay)}</span>
                    </div>

                    {loadState === "loading" ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="size-6 animate-spin text-[#4c4cf4]" />
                        </div>
                    ) : null}

                    {loadState === "error" && loadError ? (
                        <p className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-4 py-3 text-sm text-[#b91c1c]">
                            {loadError}
                        </p>
                    ) : null}

                    {loadState === "ready" && stripePromise && stripeOptions ? (
                        <Elements stripe={stripePromise} options={stripeOptions}>
                            <OfferPaymentForm
                                bookingId={bookingId}
                                providerId={providerId}
                                clientId={clientId}
                                hourlyRate={hourlyRate}
                                nbHours={nbHours}
                                cesuPlusActive={cesuPlusActive}
                                onSuccess={handleSuccess}
                            />
                        </Elements>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default OfferPaymentModal;
